import clsx from 'clsx';
import React, { ChangeEvent, useState } from 'react';
import { RegistrationFieldName } from '../../types/registration-field-name';
import { validateGender } from '../../utils/validation';
import LabelAndError from './label-and-error';

interface InputGenderProps {
  value: string;
  callback: (
    field: RegistrationFieldName | string,
    value: string | boolean,
    isValid: boolean | undefined
  ) => void;
  name: RegistrationFieldName;
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
  NonBinary = 'NonBinary',
  SelfIdentify = 'SelfIdentify',
  Other = 'Other',
  PreferNotToSay = 'PreferNotToSay',
}

export type GenderType = {
  key: Gender;
  label: string;
  selected: boolean;
};

export const GENDER_OPTIONS: GenderType[] = [
  {
    key: Gender.Male,
    label: 'Male',
    selected: false,
  },
  {
    key: Gender.Female,
    label: 'Female',
    selected: false,
  },
  {
    key: Gender.NonBinary,
    label: 'Non—Binary',
    selected: false,
  },
  {
    key: Gender.SelfIdentify,
    label: 'Self—Identify',
    selected: false,
  },
  {
    key: Gender.Other,
    label: 'Other',
    selected: false,
  },
  {
    key: Gender.PreferNotToSay,
    label: 'Prefer not to say',
    selected: false,
  },
];

const DEFAULT_VALUE = 'false';

const InputGender = React.memo(
  ({
    name,
    value = DEFAULT_VALUE,
    callback,
  }: InputGenderProps): JSX.Element => {
    const [isValid, setIsValid] = useState<undefined | boolean>();
    const [isPristine, setIsPristine] = useState(true);

    const onChangeHandler = (event: ChangeEvent<HTMLSelectElement>): void => {
      const value = event.currentTarget.value;
      const validation = validateGender(value);

      if ((!isPristine || value.length > 0) && validation) {
        setIsValid(validation);
      }

      if (isPristine && value !== DEFAULT_VALUE) {
        setIsPristine(false);
      }

      if (!isPristine && value === DEFAULT_VALUE) {
        setIsPristine(true);
      }

      callback(name, value, validation);
    };

    return (
      <label
        className={clsx('form-input', {
          'is-valid': isValid,
          'has-error': !isValid,
          [isPristine ? 'is-pristine' : 'is-touched']: true,
          'is-default-value': value === DEFAULT_VALUE,
        })}
        htmlFor={name}
      >
        <LabelAndError
          required={true}
          isValid={isValid}
          placeholder="Gender"
          errorString="Please complete this field"
        />
        <select
          name={name}
          id={name}
          onChange={onChangeHandler}
          defaultValue={value}
          required
          data-testid={name}
        >
          <option value={DEFAULT_VALUE} />
          {GENDER_OPTIONS.map((item) => (
            <option value={item.key} key={item.key}>
              {item.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
);

export default InputGender;
